import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Mail, Building2, Calendar, Search, Download, Loader2, Check, RotateCcw, ScanLine, MapPin, Eye, Trash2, X, Plus,
} from "lucide-react";
import { LeadDrawer, NewLeadModal } from "@/components/admin/PipelinePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null);


export default function LeadsPanel({ token, onExpire }) {
  const [quotes, setQuotes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [scanFilter, setScanFilter] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [active, setActive] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const updateLead = (updated) => {
    setQuotes((prev) => (prev || []).map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setActive((a) => (a && a.id === updated.id ? { ...a, ...updated } : a));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes(data);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load submissions.");
    } finally { setLoading(false); }
  }, [token, onExpire]);

  useEffect(() => { load(); }, [load]);

  const services = useMemo(() => {
    const s = new Set((quotes || []).map((q) => q.service).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [quotes]);

  const incompleteCount = useMemo(() => (quotes || []).filter((q) => q.partial).length, [quotes]);

  const filtered = useMemo(() => {
    let list = quotes || [];
    if (scanFilter) list = list.filter((q) => q.partial);
    if (statusFilter !== "all") list = list.filter((q) => (q.status || "new") === statusFilter);
    if (serviceFilter !== "all") list = list.filter((q) => q.service === serviceFilter);
    if (query.trim()) {
      const t = query.toLowerCase();
      list = list.filter((q) => [q.name, q.email, q.phone, q.business, q.service_address, q.message].filter(Boolean).some((f) => f.toLowerCase().includes(t)));
    }
    return list;
  }, [quotes, scanFilter, statusFilter, serviceFilter, query]);

  const toggleStatus = async (q) => {
    const next = (q.status || "new") === "handled" ? "new" : "handled";
    setUpdating(q.id);
    try {
      await axios.patch(`${API}/admin/quotes/${q.id}/status`, { status: next }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((prev) => prev.map((x) => (x.id === q.id ? { ...x, status: next } : x)));
      toast.success(next === "handled" ? "Marked as handled" : "Reopened lead");
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Update failed.");
    } finally { setUpdating(null); }
  };

  const restoreQuotes = async (list) => {
    try {
      const { data } = await axios.post(`${API}/admin/quotes/restore`, { quotes: list }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((prev) => {
        const ids = new Set((prev || []).map((x) => x.id));
        const add = list.filter((x) => !ids.has(x.id));
        return [...add, ...(prev || [])];
      });
      toast.success(`Restored ${data.restored} lead${data.restored === 1 ? "" : "s"}`);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Restore failed.");
    }
  };

  const deleteLead = async (q) => {
    const snapshot = { ...q };
    setDeleting(q.id);
    try {
      await axios.delete(`${API}/admin/quotes/${q.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((prev) => (prev || []).filter((x) => x.id !== q.id));
      setConfirmId(null);
      toast.success("Lead deleted", { duration: 8000, action: { label: "Undo", onClick: () => restoreQuotes([snapshot]) } });
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Delete failed.");
    } finally { setDeleting(null); }
  };

  const deleteFiltered = async () => {
    const snapshot = filtered.map((q) => ({ ...q }));
    const ids = snapshot.map((q) => q.id);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/quotes/bulk-delete`, { ids }, { headers: { Authorization: `Bearer ${token}` } });
      setQuotes((prev) => (prev || []).filter((x) => !ids.includes(x.id)));
      setBulkConfirm(false);
      toast.success(`Deleted ${data.deleted} lead${data.deleted === 1 ? "" : "s"}`, { duration: 10000, action: { label: "Undo", onClick: () => restoreQuotes(snapshot) } });
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Bulk delete failed.");
    } finally { setBulkBusy(false); }
  };

  const exportCsv = () => {
    const cols = ["name", "business", "email", "phone", "service", "service_address", "container_size", "frequency", "quantity", "current_amount", "projected_amount", "savings_annual", "partial", "message", "status", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [cols.join(","), ...filtered.map((q) => cols.map((c) => esc(q[c])).join(","))];
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `ews-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const inputCls = "w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60 transition-colors text-sm";

  if (loading && !quotes) return <div className="grid place-items-center py-20 text-slate-500"><Loader2 size={28} className="animate-spin" /></div>;

  return (
    <div className="space-y-5" data-testid="leads-panel">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[220px]">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className={inputCls} placeholder="Search name, business, address, email, phone…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="leads-search" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="leads-status-filter"
          className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60">
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="handled">Handled</option>
        </select>
        <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} data-testid="leads-service-filter"
          className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 sm:max-w-[200px]">
          {services.map((s) => <option key={s} value={s}>{s === "all" ? "All services" : s}</option>)}
        </select>
        <button onClick={() => setScanFilter((v) => !v)} data-testid="leads-scan-filter"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors ${scanFilter ? "bg-amber-500 text-white border-amber-500 hover:bg-amber-600" : "bg-white text-[#0B0F14] border-[#E7E5DF] hover:border-amber-400"}`}
          title="Show invoice scans where the visitor didn't finish the contact form">
          <ScanLine size={15} /> Incomplete scans
          {incompleteCount > 0 && (
            <span className={`grid place-items-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold ${scanFilter ? "bg-white text-amber-600" : "bg-amber-500 text-white"}`} data-testid="leads-scan-count">{incompleteCount}</span>
          )}
        </button>
        <button onClick={() => setShowNew(true)} data-testid="leads-add-lead"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">
          <Plus size={15} /> Add Lead
        </button>
        <button onClick={exportCsv} data-testid="leads-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#0E3B3B] text-white text-sm font-semibold hover:bg-[#0A2C2C] transition-colors">
          <Download size={15} /> Export CSV
        </button>
        {filtered.length > 0 && (
          bulkConfirm ? (
            <div className="flex items-center gap-2">
              <button onClick={deleteFiltered} disabled={bulkBusy} data-testid="leads-bulk-delete-confirm"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50">
                {bulkBusy ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />} Delete {filtered.length}?
              </button>
              <button onClick={() => setBulkConfirm(false)} disabled={bulkBusy} data-testid="leads-bulk-delete-cancel"
                className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#E7E5DF] text-slate-600 text-sm font-semibold hover:border-slate-400">
                <X size={15} /> Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setBulkConfirm(true)} data-testid="leads-bulk-delete"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors">
              <Trash2 size={15} /> Delete filtered ({filtered.length})
            </button>
          )
        )}
        </div>
      </div>

      <p className="text-slate-500 text-sm" data-testid="leads-count">Showing {filtered.length} of {quotes?.length || 0} leads</p>

      {filtered.length === 0 && <div className="bg-white border border-[#E7E5DF] rounded-2xl p-12 text-center text-slate-500">No leads match your filters.</div>}

      <div className="grid md:grid-cols-2 gap-5">
        {filtered.map((q) => {
          const handled = (q.status || "new") === "handled";
          const isPartial = !!q.partial;
          return (
            <div key={q.id} className={`bg-white border rounded-2xl p-5 sm:p-6 shadow-soft transition-colors ${isPartial ? "border-amber-300 bg-amber-50/40" : handled ? "border-emerald-200 bg-emerald-50/30" : "border-[#E7E5DF]"}`} data-testid="admin-quote-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold text-lg text-[#0B0F14]">{q.name}</h3>
                  {q.business && <p className="flex items-center gap-1.5 text-slate-500 text-sm mt-0.5"><Building2 size={13} /> {q.business}</p>}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={`px-3 py-1 rounded-full font-mono-tech text-[10px] uppercase tracking-wider ${handled ? "bg-slate-200 text-slate-600" : "bg-emerald-600/10 text-emerald-700"}`}>{q.service}</span>
                  {isPartial && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-mono-tech text-[10px] uppercase tracking-wider bg-amber-500 text-white" data-testid="lead-partial-badge"><ScanLine size={11} /> Incomplete scan</span>
                  )}
                </div>
              </div>
              {isPartial && <p className="mt-3 text-amber-700 text-xs font-semibold">Auto-captured from an invoice scan — the visitor has not submitted the contact form yet. Reach out to close the lead.</p>}
              {q.message && !isPartial && <p className="mt-3 text-slate-600 text-sm leading-relaxed">{q.message}</p>}
              <div className="mt-4 pt-4 border-t border-[#E7E5DF] flex flex-wrap gap-x-5 gap-y-2 text-sm">
                {q.phone && <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 text-[#0B0F14] hover:text-emerald-700"><Phone size={13} className="text-emerald-600" /> {q.phone}</a>}
                {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-[#0B0F14] hover:text-emerald-700"><Mail size={13} className="text-emerald-600" /> {q.email}</a>}
                {q.service_address && <span className="flex items-center gap-1.5 text-slate-500"><MapPin size={13} className="text-emerald-600" /> {q.service_address}</span>}
                {q.container_size && <span className="text-slate-500">{q.container_size}</span>}
                {q.frequency && <span className="text-slate-500">{q.frequency}</span>}
                {q.quantity ? <span className="text-slate-500">Qty: {q.quantity}</span> : null}
              </div>
              {(q.current_amount || q.projected_amount) && (
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm" data-testid={`lead-pricing-${q.id}`}>
                  <div className="rounded-xl bg-slate-100 px-3 py-2">
                    <p className="font-mono-tech text-[9px] uppercase tracking-wider text-slate-500">Competitor</p>
                    <p className="font-display font-bold text-[#0B0F14]">{money(q.current_amount) || "—"}<span className="text-[10px] font-normal text-slate-500">/mo</span></p>
                  </div>
                  <div className="rounded-xl bg-emerald-50 px-3 py-2">
                    <p className="font-mono-tech text-[9px] uppercase tracking-wider text-emerald-700">My price</p>
                    <p className="font-display font-bold text-emerald-700">{money(q.projected_amount) || "—"}<span className="text-[10px] font-normal">/mo</span></p>
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="flex items-center gap-1.5 font-mono-tech text-[11px] uppercase tracking-wider text-slate-400"><Calendar size={12} /> {fmtDate(q.created_at)}</p>
                <div className="flex items-center gap-2 ml-auto">
                  {confirmId === q.id ? (
                    <>
                      <button onClick={() => deleteLead(q)} disabled={deleting === q.id} data-testid={`lead-delete-confirm-${q.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                        {deleting === q.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete?
                      </button>
                      <button onClick={() => setConfirmId(null)} disabled={deleting === q.id} data-testid={`lead-delete-cancel-${q.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[#E7E5DF] text-slate-500 text-xs font-semibold hover:border-slate-400"><X size={13} /></button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmId(q.id)} title="Delete lead" data-testid={`lead-delete-${q.id}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[#E7E5DF] text-slate-400 text-xs font-semibold hover:border-red-400 hover:text-red-600 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  )}
                  <button onClick={() => setActive(q)} data-testid={`lead-view-${q.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E7E5DF] text-slate-600 text-xs font-semibold hover:border-emerald-500 hover:text-emerald-700 transition-colors">
                    <Eye size={13} /> View details
                  </button>
                  <button onClick={() => toggleStatus(q)} disabled={updating === q.id} data-testid={`lead-toggle-${q.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors disabled:opacity-50 ${handled ? "border border-[#E7E5DF] text-slate-600 hover:border-slate-400" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}>
                    {updating === q.id ? <Loader2 size={13} className="animate-spin" /> : handled ? <><RotateCcw size={13} /> Reopen</> : <><Check size={13} /> Mark handled</>}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {active && <LeadDrawer key={active.id} lead={active} token={token} onClose={() => setActive(null)} onUpdate={updateLead} />}
      {showNew && <NewLeadModal token={token} onClose={() => setShowNew(false)} onCreated={(lead) => setQuotes((prev) => [lead, ...(prev || [])])} />}
    </div>
  );
}
